// ignore-string-externalization

import React from 'react';
import styled from 'styled-components';
import {
  body1FontSize,
  body1LineHeight,
  display1FontSizeSmall,
  display2FontSizeSmall,
  display2FontSizeXSmall,
  heading1FontSizeXSmall,
  heading2LineHeight,
  heading3FontSize,
  heading3LineHeight,
  screenXsMax,
  screenSmMax,
  spacer8,
  spacer16,
  spacer24,
  spacer56,
  Type,
} from '@spotify-internal/encore-web';
import {
  DELAYS,
  withAnimationContext,
  AnimationContextType,
  fadeInAnimation,
} from '../../../ui';

const Testimony = styled.div`
  display: flex;
  flex-direction: column;
  text-align: ${(props: { centered: boolean }) =>
    props.centered ? 'center' : 'left'};
`;

const TestimonialChar = withAnimationContext(
  styled.blockquote.attrs({
    weight: 'bold',
  })<{ isHero: boolean; animationContext: AnimationContextType }>`
    ${fadeInAnimation(DELAYS.SECOND)};
    line-height: 14px;
    font-size: ${display1FontSizeSmall};
    padding: 0;
    border: 0;
    margin: 0;
    ${(props: { isHero: boolean }) =>
      props.isHero ? `padding-top: ${spacer56}` : `padding-top: ${spacer24}`};

    @media (max-width: ${screenSmMax}) {
      font-size: ${display2FontSizeSmall};
      ${(props: { isHero: boolean }) =>
        props.isHero ? `padding-top: ${spacer56}` : `padding-top: ${spacer16}`};
    }

    @media (max-width: ${screenXsMax}) {
      font-size: ${display2FontSizeXSmall};
    }
  `,
);

// TODO: the font-size and line-heights below come directly from
// figma specs.  need to find a better way to do this
const TestimonialBody = withAnimationContext(
  styled(Type.p).attrs({
    paddingBottom: '0.75em',
    variant: 'alto',
    weight: 'book',
  })<{
    centered: boolean;
    isHero: boolean;
    animationContext: AnimationContextType;
  }>`
    ${fadeInAnimation(DELAYS.THIRD)};
    @media (max-width: ${screenSmMax}) {
      ${(props: { centered: boolean; isHero: boolean }) => {
        if (props.isHero) {
          if (props.centered) {
            return `font-size: ${heading1FontSizeXSmall}; line-height: ${heading2LineHeight};`;
          }
          return `font-size: 18px; line-height: ${body1LineHeight};`;
        }
        return `font-size: ${body1FontSize}; line-height: ${body1LineHeight};`;
      }}
    }

    @media (max-width: ${screenXsMax}) {
      ${(props: { centered: boolean; isHero: boolean }) => {
        if (props.isHero) {
          if (props.centered) {
            return `font-size: ${heading3FontSize}; line-height: ${heading3LineHeight};`;
          }
        }
        return '';
      }}
    }
  `,
);

const TestimonialSource = withAnimationContext(
  styled(Type.h1).attrs({
    variant: 'celloCanon',
    weight: 'bold',
  })<{
    centered: boolean;
    isHero: boolean;
    animationContext: AnimationContextType;
  }>`
    ${fadeInAnimation(DELAYS.FOURTH)};
    padding-bottom: 0px;

    @media (max-width: ${screenSmMax}) {
      ${(props: { centered: boolean; isHero: boolean }) =>
        (props.centered && props.isHero) || !props.isHero
          ? ''
          : `visibility: hidden;`};
    }
  `,
);

const SourceTitle = withAnimationContext(
  styled(Type.p).attrs({
    variant: 'minuetBold',
  })<{
    centered: boolean;
    isHero: boolean;
    animationContext: AnimationContextType;
  }>`
    ${fadeInAnimation(DELAYS.FIFTH)};
    padding-top: ${spacer8};

    @media (max-width: ${screenSmMax}) {
      ${(props: { centered: boolean; isHero: boolean }) =>
        (props.centered && props.isHero) || !props.isHero
          ? ''
          : `visibility: hidden;`};
    }
  `,
);

export type TestimonialType = {
  quote?: string;
  source?: string;
  jobTitle?: string;
  centered?: boolean;
  isHero?: boolean;
};

export const Testimonial: React.FC<TestimonialType> = ({
  quote = '',
  source = '',
  jobTitle = '',
  centered = false,
  isHero = false,
}) => {
  return (
    <Testimony centered={centered}>
      <TestimonialChar isHero={isHero} className="quote-char">
        “
      </TestimonialChar>
      <TestimonialBody centered={centered} isHero={isHero}>
        {quote}
      </TestimonialBody>
      <TestimonialSource centered={centered} isHero={isHero}>
        {' '}
        &mdash; {source}
      </TestimonialSource>
      <SourceTitle centered={centered} isHero={isHero}>
        {jobTitle}
      </SourceTitle>
    </Testimony>
  );
};
